/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateUserGroupRequest } from '../models/create-user-group-request';
import { GenericResponse } from '../models/generic-response';
import { GetUserGroupResponse } from '../models/get-user-group-response';
import { UpdateUserGroupRequest } from '../models/update-user-group-request';
import { UserGroupResponse } from '../models/user-group-response';

/**
 * User Group APIs
 */
@Injectable({ providedIn: 'root' })
export class UserGroupsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllUserGroups()` */
  static readonly GetAllUserGroupsPath = '/api/v1/user-groups';

  /**
   * Get list of user group.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllUserGroups()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUserGroups$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;

      /**
       * this adds an id search to the following fields:
       *   - user
       */
      userId?: number;

      /**
       * this adds a filter to the following field:
       *   - senderId
       */
      senderId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetUserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.GetAllUserGroupsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('userId', params.userId, {});
      rb.query('senderId', params.senderId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetUserGroupResponse>;
        })
      );
  }

  /**
   * Get list of user group.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllUserGroups$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllUserGroups(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;

      /**
       * this adds an id search to the following fields:
       *   - user
       */
      userId?: number;

      /**
       * this adds a filter to the following field:
       *   - senderId
       */
      senderId?: number;
    },
    context?: HttpContext
  ): Observable<GetUserGroupResponse> {
    return this.getAllUserGroups$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetUserGroupResponse>): GetUserGroupResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createUserGroup()` */
  static readonly CreateUserGroupPath = '/api/v1/user-groups';

  /**
   * Create new User Group.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createUserGroup()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUserGroup$Response(
    params: {
      body: CreateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.CreateUserGroupPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupResponse>;
        })
      );
  }

  /**
   * Create new User Group.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createUserGroup$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createUserGroup(
    params: {
      body: CreateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<UserGroupResponse> {
    return this.createUserGroup$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserGroupResponse>): UserGroupResponse => r.body
      )
    );
  }

  /** Path part for operation `getUserGroupById()` */
  static readonly GetUserGroupByIdPath = '/api/v1/user-groups/{id}';

  /**
   * Get user group details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUserGroupById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserGroupById$Response(
    params: {
      /**
       * The user group's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.GetUserGroupByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupResponse>;
        })
      );
  }

  /**
   * Get user group details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUserGroupById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUserGroupById(
    params: {
      /**
       * The user group's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<UserGroupResponse> {
    return this.getUserGroupById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserGroupResponse>): UserGroupResponse => r.body
      )
    );
  }

  /** Path part for operation `disableUserGroup()` */
  static readonly DisableUserGroupPath = '/api/v1/user-groups/{id}';

  /**
   * disable user group.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableUserGroup()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableUserGroup$Response(
    params: {
      /**
       * The user group's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.DisableUserGroupPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable user group.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableUserGroup$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableUserGroup(
    params: {
      /**
       * The user group's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableUserGroup$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateUserGroupById()` */
  static readonly UpdateUserGroupByIdPath = '/api/v1/user-groups/{id}';

  /**
   * Update user group by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateUserGroupById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUserGroupById$Response(
    params: {
      /**
       * The user group's id that needs to be updated
       */
      id: number;
      body: UpdateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<UserGroupResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      UserGroupsService.UpdateUserGroupByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<UserGroupResponse>;
        })
      );
  }

  /**
   * Update user group by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateUserGroupById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateUserGroupById(
    params: {
      /**
       * The user group's id that needs to be updated
       */
      id: number;
      body: UpdateUserGroupRequest;
    },
    context?: HttpContext
  ): Observable<UserGroupResponse> {
    return this.updateUserGroupById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<UserGroupResponse>): UserGroupResponse => r.body
      )
    );
  }
}
